"use client";

import React from "react";
import Image from "next/image";
import { X } from "lucide-react";
import { useFormState } from "react-hook-form";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import Link from "@/components/link";
import { Button } from "@/components/ui/button";
import { FormField, FormItem, FormMessage } from "@/components/ui/form";

import fetcher from "@/lib/fetcher";
import { cn } from "@/lib/utils";

import "swiper/css";

function AuthScenePhone({ onClose }) {
  const { isSubmitting } = useFormState();

  const [slides, setSlides] = React.useState([]);

  const handleNumericKeyDown = (e) => {
    // Allow numeric keys (0-9)
    const isNumber = /^[0-9]$/.test(e.key);

    // Allow essential keys for navigation and editing
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "Home",
      "End",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ];

    // Allow Ctrl/Cmd combinations (like Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z)
    const isCtrlKey = e.ctrlKey || e.metaKey;

    // Allow selection (Shift + Arrow keys)
    const isSelectionKey =
      e.shiftKey &&
      (e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Home" ||
        e.key === "End");

    // Prevent default if none of the allowed conditions are met
    if (
      !isNumber &&
      !allowedKeys.includes(e.key) &&
      !isCtrlKey &&
      !isSelectionKey
    ) {
      e.preventDefault();
    }
  };

  const handleNumericPaste = (e, field) => {
    // Handle paste events to filter non-numeric characters
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const numericText = pastedText.replace(/[^0-9]/g, "");
    const newValue = field.value + numericText;
    field.onChange(newValue.slice(0, 10)); // Respect maxLength
  };

  React.useEffect(() => {
    fetcher("/api/web/loginslider/web")
      .then((response) => response.json())
      .then((response) => {
        setSlides(response?.data?.slides || []);
      });
  }, []);

  return (
    <>
      <div className="flex items-center gap-x-2 -mx-7 mb-8">
        <Button
          variant="link"
          size="icon"
          className="size-6"
          type="button"
          onClick={onClose}
        >
          <X size={18} />
        </Button>
      </div>

      <div className="flex flex-col justify-center h-full">
        <Swiper
          className="w-full"
          modules={[Pagination, Autoplay]}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
          }}
          initialSlide={1}
          loop
        >
          {slides.map((slide) => (
            <SwiperSlide
              key={slide.image_url}
              className="flex! flex-col items-center gap-y-1"
            >
              <div className="relative min-h-[35vh] aspect-square">
                <Image
                  src={slide.image_url}
                  alt={slide.description1}
                  sizes="37.5rem"
                  fill
                />
              </div>

              <div className="flex flex-col gap-y-1 text-gray-400/80 font-extralight text-sm text-center">
                <span className="font-medium">{slide.description1}</span>
                <span className="font-medium">{slide.description2}</span>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="flex flex-col gap-y-4 px-6 mt-4">
          <div className="flex items-center gap-x-4">
            <span className="flex-1 h-px rounded-full bg-gray-200" />
            <span className="text-sm font-medium text-gray-800">
              Sign Up or Login
            </span>
            <span className="flex-1 h-px rounded-full bg-gray-200" />
          </div>

          <div className="flex flex-col gap-y-4 py-2">
            <div className="flex flex-col gap-y-2">
              <FormField
                name="phone"
                rules={{
                  required: true,
                  minLength: {
                    value: 10,
                    message: "Please enter a valid phone number.",
                  },
                  maxLength: {
                    value: 10,
                    message: "Please enter a valid phone number.",
                  },
                  pattern: {
                    value: /^[0-9]+$/,
                    message:
                      "Please enter a valid phone number (10 digits only).",
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <label
                      htmlFor="phone"
                      className={cn(
                        "flex items-center gap-x-2.5 border border-primary/40 rounded-md",
                        "text-sm font-medium text-gray-800 h-9.5"
                      )}
                    >
                      <span className="px-2.5 inline-block border-r border-gray-300 text-primary/60">
                        +91
                      </span>
                      <input
                        type="text"
                        id="phone"
                        placeholder="Enter 10 digit number"
                        maxLength={10}
                        className="w-full outline-none! text-gray-700"
                        onKeyDown={handleNumericKeyDown}
                        onPaste={(e) => handleNumericPaste(e, field)}
                        {...field}
                      />
                    </label>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button
              type="submit"
              size="small"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              Continue
            </Button>
          </div>

          <p className="block mt-4 text-sm text-gray-600 font-light text-center">
            By continuing you agree with our{" "}
            <Link href="/terms-and-conditions" className="text-primary">
              terms and conditions
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}

export default AuthScenePhone;
